/**
 * Types + fetcher dla stanu MFA (2FA) - konsumowane przez MfaGuard oraz
 * ProfileTabSecurity w shellu c_user_accounts.
 *
 * Backend: `GET /IRESTAPI/v1/IIAPI_CONTROLLER/mfaStatus`, proxy w
 * `app/api/iiapi/user/mfa/status/route.ts`. Response: `{ available, option,
 * enabled }` (patrz `MfaStatusResponse.json` w `web/api/v1/components/schemas`).
 *
 * `option` to agregacja `imp_user_group.2fa_option` z grup usera (MAX);
 * pojedyncze źródło prawdy: `IUSER::getMfaOption` w PHP.
 */

export type MfaOption = "disabled" | "optional" | "required";

export type MfaStatus = {
  /** Rozszerzenie domeny `imperial_TFA_login` włączone. `false` -> option zawsze 'disabled'. */
  available: boolean;
  /** Polityka MFA agregowana z grup usera (MAX). */
  option: MfaOption;
  /** User ma aktywny secret + flagę `enabled_tfa=1`. */
  enabled: boolean;
};

/**
 * Pobiera aktualny stan MFA usera. Fail-soft: przy błędzie sieci zwraca stan
 * "wszystko wyłączone" - MfaGuard przepuszcza content bez blokady (nie
 * blokujemy konta z powodu chwilowego problemu z API).
 */
/**
 * Nazwa custom window event dispatched'owanego po każdej zmianie stanu MFA
 * (enable/disable w `ProfileTabSecurity`). Konsumenci (np. `MfaGuard`) mogą
 * nasłuchiwać żeby odświeżyć swój lokalny cache statusu bez ręcznego pull.
 *
 * Cross-component signal potrzebny bo `hashchange` NIE odpala się gdy user
 * zapisuje MFA zostając na tym samym `#security` - MfaGuard bez tego event'u
 * pokazywałby banner nawet po udanej konfiguracji, dopóki user nie odświeżył
 * strony.
 */
export const MFA_CHANGED_EVENT = "mfa:changed";

/** Helper żeby dispatch był typowany i spójny (bez rozjazdu literałów). */
export function dispatchMfaChanged(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(MFA_CHANGED_EVENT));
  }
}

export async function fetchMfaStatus(): Promise<MfaStatus> {
  const fallback: MfaStatus = { available: false, option: "disabled", enabled: false };
  try {
    const res = await fetch("/api/iiapi/user/mfa/status", { cache: "no-store" });
    if (!res.ok) return fallback;
    const body = (await res.json()) as { ok?: boolean; data?: MfaStatus } | null;
    if (!body || body.ok !== true || !body.data) return fallback;
    return {
      available: body.data.available === true,
      option: body.data.option ?? "disabled",
      enabled: body.data.enabled === true,
    };
  } catch {
    return fallback;
  }
}
